import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Plus, Edit, X, Coins, Archive } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import EditableCell from "./EditableCell";
import AppCreditsEditor from "./AppCreditsEditor";
import {
  useGetPlansQuery,
  useGetEntitlementVocabularyQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useGetArchivedPlansQuery,
  useRestorePlanMutation,
  type Plan,
  type AppPlan,
} from "@/redux/api/plans.rtkq";
import { useGetAppsQuery } from "@/redux/api/apps.rtkq";

const CONNECTOR_LABELS: Record<string, string> = {
  "mcp-connector": "MCP",
  "agent-connector": "Agent",
  "addin-connector": "Add-in",
  "webapp-connector": "Web app",
};

const AGENT_CLOUD = "agent-cloud";
const MCP_CONNECTOR = "mcp-connector";
const AGENT_CONNECTOR = "agent-connector";
const MCP_APP_ID = "mcp";

const DEFAULT_CONNECTORS = [
  MCP_CONNECTOR,
  AGENT_CONNECTOR,
  "addin-connector",
  "webapp-connector",
];

/** Soft unlimited sentinel — mirrors backend UNLIMITED_INCLUDED_CREDITS. */
const UNLIMITED_INCLUDED_CREDITS = 1_000_000;

type IncludedAppForm = {
  appIdentifier: string;
  price: number;
  includedCredits?: number;
  creditCost?: number;
  unlimited?: boolean;
};

/** Old plans store `mcp`; show the new connector checkbox as checked. */
function featuresForForm(raw: string[] | undefined): string[] {
  const set = new Set(raw ?? []);
  if (set.has("mcp")) set.add(MCP_CONNECTOR);
  if (set.has(AGENT_CLOUD)) set.add(AGENT_CONNECTOR);
  return [...set];
}

/** Agent checkbox owns cloud mode. Drop legacy `mcp` feature. */
function featuresForSave(features: string[] | undefined): string[] {
  const set = new Set((features ?? []).filter((f) => f !== "mcp"));
  if (set.has(AGENT_CONNECTOR)) set.add(AGENT_CLOUD);
  else set.delete(AGENT_CLOUD);
  return [...set];
}

function defaultManagedApp(appIdentifier: string): IncludedAppForm {
  return {
    appIdentifier,
    price: 0,
    includedCredits: UNLIMITED_INCLUDED_CREDITS,
    creditCost: 1,
    unlimited: true,
  };
}

/** MCP checkbox owns the `mcp` billing wallet — do not pick it from the app list. */
function syncManagedWallets(
  apps: IncludedAppForm[],
  features: string[] | undefined,
): IncludedAppForm[] {
  const mcpOn = (features ?? []).includes(MCP_CONNECTOR);
  const withoutMcp = apps.filter((app) => app.appIdentifier !== MCP_APP_ID);
  if (!mcpOn) return withoutMcp;
  const existing = apps.find((app) => app.appIdentifier === MCP_APP_ID);
  return [...withoutMcp, existing ?? defaultManagedApp(MCP_APP_ID)];
}

function isManagedChannelWallet(appIdentifier: string): boolean {
  return appIdentifier === MCP_APP_ID;
}

// Form schema
const planFormSchema = z.object({
  name: z.string().min(1, "שם התוכנית נדרש"),
  internalName: z.string().min(1, "שם פנימי נדרש"),
  priceMonthly: z.number().min(0, "מחיר חודשי חייב להיות חיובי"),
  monthlyCreditAllocation: z.number().min(0, "קרדיטים חודשיים חייבים להיות חיוביים"),
  periodDays: z.number().min(1),
  quotaWindowDays: z.number().min(1),
  description: z.string().optional(),
  features: z.array(z.string()).optional(),
  includedApps: z.array(
    z.object({
      appIdentifier: z.string(),
      price: z.number().min(0, "מחיר חייב להיות חיובי"),
      includedCredits: z.number().optional(),
      creditCost: z.number().optional(),
      unlimited: z.boolean().optional(),
    })
  ),
});

type PlanFormData = z.infer<typeof planFormSchema>;

const toCreditApps = (
  apps: Array<{
    appIdentifier: string;
    price?: number;
    includedCredits?: number;
    creditCost?: number;
    unlimited?: boolean;
  }>,
): AppPlan[] =>
  apps.map((app) => {
    const unlimited = app.unlimited === true;
    return {
      appIdentifier: app.appIdentifier,
      price: app.price ?? 0,
      includedCredits: unlimited
        ? UNLIMITED_INCLUDED_CREDITS
        : (app.includedCredits ?? 0),
      creditCost: app.creditCost ?? 1,
      unlimited,
    };
  });

const isFreePlanInternalName = (name?: string | null): boolean => {
  const n = name?.trim().toLowerCase();
  return n === "free" || n === "free-agent-cloud";
};

const isAppUnlimited = (app: { unlimited?: boolean; includedCredits?: number }): boolean =>
  app.unlimited === true || (app.includedCredits ?? 0) >= UNLIMITED_INCLUDED_CREDITS;

/** Calendar-month denominator for the quota-window fill. Mirrors backend QUOTA_MONTH_DAYS. */
const QUOTA_MONTH_DAYS = 30;

function windowCreditsForApp(includedCredits: number, windowDays: number): number {
  if (!Number.isFinite(includedCredits) || includedCredits <= 0) return 0;
  const window = windowDays > 0 ? windowDays : 3;
  return Math.floor((includedCredits * window) / QUOTA_MONTH_DAYS);
}

function PlanFeaturesFields({
  form,
  connectors,
}: {
  form: ReturnType<typeof useForm<PlanFormData>>;
  connectors: string[];
}) {
  const features = form.watch("features") || [];
  const toggle = (value: string, on: boolean) => {
    const current = form.getValues("features") || [];
    let next = on
      ? Array.from(new Set([...current, value]))
      : current.filter((f) => f !== value);
    if (value === AGENT_CONNECTOR) {
      next = on
        ? Array.from(new Set([...next, AGENT_CLOUD]))
        : next.filter((f) => f !== AGENT_CLOUD);
    }
    form.setValue("features", next, { shouldDirty: true });
  };

  return (
    <div className="space-y-2">
      <Label className="text-base font-medium">ערוצי גישה (connectors)</Label>
      <div className="flex flex-wrap gap-4">
        {connectors.map((value) => {
          const checked = features.includes(value);
          return (
            <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={checked}
                onCheckedChange={(next) => toggle(value, next === true)}
              />
              <span>{CONNECTOR_LABELS[value] || value}</span>
              <span className="text-xs text-muted-foreground font-mono" dir="ltr">
                ({value})
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function IncludedAppsTable({
  form,
  appFields,
  apps,
  watchedApps,
  quotaWindowDays,
  removeApp,
}: {
  form: ReturnType<typeof useForm<PlanFormData>>;
  appFields: Array<{ id: string; appIdentifier: string }>;
  apps: Array<{ appIdentifier: string; name: string }>;
  watchedApps: PlanFormData["includedApps"];
  quotaWindowDays: number;
  removeApp: (index: number) => void;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="max-h-96 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-right">אפליקציה</TableHead>
              <TableHead className="text-right">ללא הגבלה</TableHead>
              <TableHead className="text-right">קרדיטים כלולים</TableHead>
              <TableHead className="text-right">קרדיטי חלון</TableHead>
              <TableHead className="text-right">עלות קרדיט</TableHead>
              <TableHead className="text-right">פעולות</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {appFields.map((field, index) => {
              if (isManagedChannelWallet(field.appIdentifier)) return null;
              const app = apps.find((a) => a.appIdentifier === field.appIdentifier);
              const unlimited = watchedApps[index]?.unlimited === true;
              const monthlyCredits = watchedApps[index]?.includedCredits || 0;
              return (
                <TableRow key={field.id}>
                  <TableCell className="font-medium text-right">
                    {app?.name || field.appIdentifier}
                  </TableCell>
                  <TableCell className="text-right">
                    <FormField
                      control={form.control}
                      name={`includedApps.${index}.unlimited`}
                      render={({ field: unlimitedField }) => (
                        <Checkbox
                          checked={unlimitedField.value === true}
                          onCheckedChange={(next) => {
                            const on = next === true;
                            unlimitedField.onChange(on);
                            if (on) {
                              form.setValue(
                                `includedApps.${index}.includedCredits`,
                                UNLIMITED_INCLUDED_CREDITS,
                              );
                            } else if (
                              (form.getValues(`includedApps.${index}.includedCredits`) || 0) >=
                              UNLIMITED_INCLUDED_CREDITS
                            ) {
                              form.setValue(`includedApps.${index}.includedCredits`, 0);
                            }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <FormField
                      control={form.control}
                      name={`includedApps.${index}.includedCredits`}
                      render={({ field: creditsField }) => (
                        <Input
                          type="number"
                          className="w-20"
                          disabled={unlimited}
                          {...creditsField}
                          value={unlimited ? "" : creditsField.value}
                          placeholder={unlimited ? "∞" : "0"}
                          onChange={(e) =>
                            creditsField.onChange(parseInt(e.target.value) || 0)
                          }
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {unlimited ? (
                      <span className="inline-block w-20 text-center font-medium">∞</span>
                    ) : (
                      <Input
                        type="number"
                        className="w-20"
                        value={windowCreditsForApp(monthlyCredits, quotaWindowDays)}
                        disabled
                        readOnly
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <FormField
                      control={form.control}
                      name={`includedApps.${index}.creditCost`}
                      render={({ field: costField }) => (
                        <Input
                          type="number"
                          placeholder="1"
                          className="w-16"
                          {...costField}
                          onChange={(e) =>
                            costField.onChange(parseInt(e.target.value) || 1)
                          }
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeApp(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CreditsSummary({
  watchedApps,
  apps,
  quotaWindowDays,
  totalCredits,
  totalWindowCredits,
  hasUnlimited,
}: {
  watchedApps: PlanFormData["includedApps"];
  apps: Array<{ appIdentifier: string; name: string }>;
  quotaWindowDays: number;
  totalCredits: number;
  totalWindowCredits: number;
  hasUnlimited: boolean;
}) {
  if (watchedApps.filter((app) => !isManagedChannelWallet(app.appIdentifier)).length === 0) {
    return null;
  }
  return (
    <Card className="p-4 bg-indigo-50">
      <h4 className="font-medium text-indigo-900 mb-3">סיכום חישוב</h4>
      <div className="space-y-2">
        {watchedApps.map((app, index) => {
          if (isManagedChannelWallet(app.appIdentifier)) return null;
          const appData = apps.find((a) => a.appIdentifier === app.appIdentifier);
          const unlimited = app.unlimited === true;
          const monthly = app.includedCredits || 0;
          const windowCredits = windowCreditsForApp(monthly, quotaWindowDays);
          return (
            <div key={index} className="flex justify-between text-sm">
              <span>{appData?.name || app.appIdentifier}</span>
              <span className="font-medium text-slate-600">
                {unlimited
                  ? "∞"
                  : monthly
                    ? `${monthly} חודשי / ${windowCredits} חלון`
                    : "0 קרדיטים"}
              </span>
            </div>
          );
        })}
        <div className="border-t pt-2 flex justify-between font-semibold">
          <span>סה"כ</span>
          <span>
            {hasUnlimited && totalCredits === 0
              ? "∞"
              : hasUnlimited
                ? `∞ + ${totalCredits} חודשי / ${totalWindowCredits} חלון`
                : `${totalCredits} חודשי / ${totalWindowCredits} חלון`}
          </span>
        </div>
      </div>
    </Card>
  );
}

const PlansManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [quotasPlan, setQuotasPlan] = useState<Plan | null>(null);
  const [quotaDraft, setQuotaDraft] = useState<AppPlan[]>([]);
  const [planTab, setPlanTab] = useState<"active" | "archived">("active");

  // RTK Query hooks
  const { data: plans = [], isLoading, error } = useGetPlansQuery();
  const { data: archivedPlans = [], isLoading: archivedLoading } = useGetArchivedPlansQuery();
  const { data: vocabulary } = useGetEntitlementVocabularyQuery();
  const connectors = vocabulary?.connectors?.length
    ? vocabulary.connectors
    : DEFAULT_CONNECTORS;
  const { data: apps = [], isLoading: appsLoading } = useGetAppsQuery();
  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();
  const [deletePlan, { isLoading: isDeleting }] = useDeletePlanMutation();
  const [restorePlan, { isLoading: isRestoring }] = useRestorePlanMutation();

  // Form setup
  const form = useForm<PlanFormData>({
    resolver: zodResolver(planFormSchema),
    defaultValues: {
      name: "",
      internalName: "",
      priceMonthly: 0,
      monthlyCreditAllocation: 0,
      periodDays: 30,
      quotaWindowDays: 3,
      description: "",
      features: [],
      includedApps: [],
    },
  });

  const {
    fields: appFields,
    append: appendApp,
    remove: removeApp,
  } = useFieldArray({
    control: form.control,
    name: "includedApps",
  });

  // Watch includedApps to calculate totals
  const watchedApps = form.watch("includedApps");
  const watchedInternalName = form.watch("internalName");
  const watchedQuotaWindowDays = form.watch("quotaWindowDays");
  const isEditingFreePlan = isFreePlanInternalName(watchedInternalName);
  const quotaWindowDays = watchedQuotaWindowDays > 0 ? watchedQuotaWindowDays : 3;

  // Calculate credit totals from apps (exclude soft-unlimited so monthlyCreditAllocation stays sane)
  const calculateTotals = () => {
    const limited = watchedApps.filter(
      (app) => !app.unlimited && !isManagedChannelWallet(app.appIdentifier),
    );
    const totalCredits = limited.reduce(
      (sum, app) => sum + (app.includedCredits || 0),
      0,
    );
    const totalWindowCredits = limited.reduce(
      (sum, app) => sum + windowCreditsForApp(app.includedCredits || 0, quotaWindowDays),
      0,
    );
    const hasUnlimited = watchedApps.some(
      (app) => app.unlimited === true && !isManagedChannelWallet(app.appIdentifier),
    );
    return { totalCredits, totalWindowCredits, hasUnlimited };
  };

  const { totalCredits, totalWindowCredits, hasUnlimited } = calculateTotals();

  // Keep monthlyCreditAllocation in sync with per-app included credits (limited only)
  React.useEffect(() => {
    form.setValue("monthlyCreditAllocation", totalCredits);
  }, [totalCredits, form]);

  const mcpConnectorOn = (form.watch("features") ?? []).includes(MCP_CONNECTOR);
  React.useEffect(() => {
    const appsNow = form.getValues("includedApps") ?? [];
    const idx = appsNow.findIndex((app) => app.appIdentifier === MCP_APP_ID);
    if (mcpConnectorOn && idx < 0) {
      appendApp(defaultManagedApp(MCP_APP_ID));
    } else if (!mcpConnectorOn && idx >= 0) {
      removeApp(idx);
    }
  }, [mcpConnectorOn, appendApp, removeApp, form]);

  const visibleAppFields = appFields.filter(
    (field) => !isManagedChannelWallet(field.appIdentifier),
  );

  const handleCreatePlan = async (data: PlanFormData) => {
    try {
      const planData: Plan = {
        name: data.name,
        internalName: data.internalName,
        priceMonthly: data.priceMonthly,
        monthlyCreditAllocation: data.monthlyCreditAllocation,
        periodDays: data.periodDays,
        quotaWindowDays: data.quotaWindowDays,
        includedApps: toCreditApps(syncManagedWallets(data.includedApps, data.features)),
        features: featuresForSave(data.features),
        description: data.description,
      };

      await createPlan(planData).unwrap();
      form.reset();
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create plan:", error);
    }
  };

  const handleEditPlan = async (data: PlanFormData) => {
    if (!editingPlan?.planId) return;

    try {
      const updates: Partial<Plan> = {
        name: data.name,
        internalName: data.internalName,
        priceMonthly: data.priceMonthly,
        monthlyCreditAllocation: data.monthlyCreditAllocation,
        periodDays: data.periodDays,
        quotaWindowDays: data.quotaWindowDays,
        monthlyBudgetUsd: editingPlan?.monthlyBudgetUsd ?? 0,
        includedApps: toCreditApps(syncManagedWallets(data.includedApps, data.features)),
        features: featuresForSave(data.features),
        description: data.description,
      };

      await updatePlan({ planId: editingPlan.planId, updates }).unwrap();
      setEditingPlan(null);
      form.reset();
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to update plan:", error);
    }
  };

  const planErrorMessage = (error: unknown): string => {
    const data = (error as { data?: { message?: string | string[] } })?.data;
    const message = data?.message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string" && message.trim()) return message;
    return "לא ניתן לארכב את התוכנית";
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      await deletePlan({ planId }).unwrap();
    } catch (error) {
      toast.error(planErrorMessage(error));
    }
  };

  const handleRestorePlan = async (planId: string) => {
    try {
      await restorePlan({ planId }).unwrap();
      toast.success("התוכנית שוחזרה");
    } catch (error) {
      toast.error(planErrorMessage(error));
    }
  };

  const openQuotasDialog = (plan: Plan) => {
    setQuotasPlan(plan);
    setQuotaDraft(
      (plan.includedApps || []).map((app) => ({
        ...app,
        unlimited: isAppUnlimited(app),
        includedCredits: isAppUnlimited(app)
          ? UNLIMITED_INCLUDED_CREDITS
          : Number(app.includedCredits) || 0,
        creditCost: Number(app.creditCost) || 1,
      })),
    );
  };

  const handleSavePlanQuotas = async () => {
    if (!quotasPlan?.planId) return;
    const monthlyCreditAllocation = quotaDraft.reduce(
      (sum, app) =>
        sum + (isAppUnlimited(app) ? 0 : Number(app.includedCredits) || 0),
      0,
    );
    try {
      await updatePlan({
        planId: quotasPlan.planId,
        updates: {
          includedApps: toCreditApps(
            syncManagedWallets(quotaDraft, quotasPlan.features),
          ),
          monthlyCreditAllocation,
        },
      }).unwrap();
      toast.success("מכסות האפליקציות עודכנו");
      setQuotasPlan(null);
    } catch (error) {
      console.error("Failed to update plan quotas:", error);
      toast.error("שגיאה בעדכון המכסות");
    }
  };

  const openEditDialog = (plan: Plan) => {
    setEditingPlan(plan);
    const features = featuresForForm(plan.features);
    const includedApps = syncManagedWallets(
      (plan.includedApps || []).map((app) => ({
        appIdentifier: app.appIdentifier,
        price: Number(app.price) || 0,
        unlimited: isAppUnlimited(app),
        includedCredits: isAppUnlimited(app)
          ? UNLIMITED_INCLUDED_CREDITS
          : Number(app.includedCredits) || 0,
        creditCost: Number(app.creditCost) || 1,
      })),
      features,
    );

    form.reset({
      name: plan.name,
      internalName: plan.internalName || "",
      priceMonthly: plan.priceMonthly,
      monthlyCreditAllocation: plan.monthlyCreditAllocation,
      periodDays: plan.periodDays ?? 30,
      quotaWindowDays: plan.quotaWindowDays ?? 3,
      description: plan.description || "",
      features,
      includedApps,
    });
    setIsEditDialogOpen(true);
  };

  const addAppToPlan = (appIdentifier: string) => {
    if (isManagedChannelWallet(appIdentifier)) return;
    const app = apps.find((a) => a.appIdentifier === appIdentifier);
    if (app) {
      appendApp({
        appIdentifier: app.appIdentifier,
        price: 0,
        includedCredits: 0,
        creditCost: 1,
        unlimited: false,
      });
    }
  };

  if (isLoading || appsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">טוען תוכניות...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">שגיאה בטעינת התוכניות</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">ניהול תוכניות מנוי</h3>
          <p className="text-sm text-slate-600">צור, ערוך ומחק תוכניות מנוי במערכת</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white">
              <Plus className="h-4 w-4 ml-2" />
              תוכנית חדשה
            </Button>
          </DialogTrigger>
          <DialogContent className="h-[90vh] flex flex-col lg:max-w-5xl xl:max-w-7xl">
            <DialogHeader>
              <DialogTitle>יצירת תוכנית חדשה</DialogTitle>
              <DialogDescription>מלא את הפרטים לתוכנית המנוי החדשה</DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreatePlan)} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>שם התוכנית</FormLabel>
                          <FormControl>
                            <Input placeholder="לדוגמה: חבילה מקצועית" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="internalName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>שם פנימי</FormLabel>
                          <FormControl>
                            <Input placeholder="professional_plan" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>תיאור</FormLabel>
                        <FormControl>
                          <Input placeholder="תיאור קצר של התוכנית" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="quotaWindowDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ימי חלון קצובה</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isEditingFreePlan && (
                    <FormField
                      control={form.control}
                      name="periodDays"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ימי מנוי</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value) || 30)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <PlanFeaturesFields
                    form={form}
                    connectors={connectors}
                  />

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base font-medium">אפליקציות כלולות</Label>
                      <Select onValueChange={addAppToPlan}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="הוסף אפליקציה" />
                        </SelectTrigger>
                        <SelectContent>
                          {apps
                            .filter(
                              (app) =>
                                !isManagedChannelWallet(app.appIdentifier) &&
                                !appFields.some((field) => field.appIdentifier === app.appIdentifier),
                            )
                            .map((app) => (
                              <SelectItem key={app.appIdentifier} value={app.appIdentifier}>
                                {app.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {visibleAppFields.length > 0 ? (
                      <IncludedAppsTable
                        form={form}
                        appFields={appFields}
                        apps={apps}
                        watchedApps={watchedApps}
                        quotaWindowDays={quotaWindowDays}
                        removeApp={removeApp}
                      />
                    ) : (
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                        <p className="text-slate-500">אין אפליקציות בתוכנית</p>
                        <p className="text-sm text-slate-400 mt-1">הוסף אפליקציה מהרשימה למעלה</p>
                      </div>
                    )}
                  </div>

                  <CreditsSummary
                    watchedApps={watchedApps}
                    apps={apps}
                    quotaWindowDays={quotaWindowDays}
                    totalCredits={totalCredits}
                    totalWindowCredits={totalWindowCredits}
                    hasUnlimited={hasUnlimited}
                  />
                </div>

                <DialogFooter className="flex-shrink-0 mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateDialogOpen(false);
                      form.reset();
                    }}
                  >
                    ביטול
                  </Button>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? "יוצר..." : "צור תוכנית"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>תוכניות קיימות</CardTitle>
          <CardDescription>רשימת כל תוכניות המנוי במערכת</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={planTab} onValueChange={(value) => setPlanTab(value as "active" | "archived")} dir="rtl">
            <TabsList className="mb-4">
              <TabsTrigger value="active">פעילות</TabsTrigger>
              <TabsTrigger value="archived">ארכיון</TabsTrigger>
            </TabsList>
            <TabsContent value="active">
          {plans.length === 0 ? (
            <div className="text-center py-8 text-slate-600">אין תוכניות זמינות. צור תוכנית ראשונה כדי להתחיל.</div>
          ) : (
            <div className="overflow-x-auto">
            <Table className="text-right">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">שם התוכנית</TableHead>
                  <TableHead className="text-right">מחיר חודשי</TableHead>
                  <TableHead className="text-right">ימי מנוי</TableHead>
                  <TableHead className="text-right">ברירת מחדל להרשמה</TableHead>
                  <TableHead className="text-right">מכסות לפי אפליקציה</TableHead>
                  <TableHead className="text-right">תקציב חודשי ($)</TableHead>
                  <TableHead className="text-right">משרדים</TableHead>
                  <TableHead className="text-right">אפליקציות</TableHead>
                  <TableHead className="text-right">פעולות</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.planId}>
                    <TableCell className="text-right">
                      <EditableCell
                        value={plan.name}
                        onSave={async (val) => { await updatePlan({ planId: plan.planId!, updates: { name: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={<span className="font-medium">{plan.name}</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={plan.priceMonthly}
                        onSave={async (val) => { await updatePlan({ planId: plan.planId!, updates: { priceMonthly: val as number } }).unwrap(); }}
                        type="number"
                        displayValue={plan.priceMonthly === 0 ? <Badge variant="secondary">חינמי</Badge> : <span>₪{plan.priceMonthly}</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {isFreePlanInternalName(plan.internalName) ? (
                        <span className="text-sm text-muted-foreground">ללא תוקף</span>
                      ) : (
                        <EditableCell
                          value={plan.periodDays ?? 30}
                          onSave={async (val) => { await updatePlan({ planId: plan.planId!, updates: { periodDays: val as number } }).unwrap(); }}
                          type="number"
                          displayValue={<span>{plan.periodDays ?? 30}</span>}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={Boolean(plan.isSignupDefault)}
                        onSave={async (val) => {
                          try {
                            await updatePlan({
                              planId: plan.planId!,
                              updates: { isSignupDefault: val as boolean },
                            }).unwrap();
                          } catch (error) {
                            console.error("Failed to update signup default:", error);
                            toast.error("לא ניתן לסמן תוכנית ללא אפליקציות כברירת מחדל");
                            throw error;
                          }
                        }}
                        type="toggle"
                        displayValue={
                          plan.isSignupDefault ? (
                            <Badge variant="default" className="bg-emerald-600">הרשמה</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => openQuotasDialog(plan)}
                      >
                        <Coins className="h-4 w-4" />
                        מכסות לפי אפליקציה
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={plan.monthlyBudgetUsd ?? 0}
                        onSave={async (val) => { await updatePlan({ planId: plan.planId!, updates: { monthlyBudgetUsd: val as number } }).unwrap(); }}
                        type="number"
                        displayValue={
                          plan.monthlyBudgetUsd && plan.monthlyBudgetUsd > 0
                            ? <span>${plan.monthlyBudgetUsd}</span>
                            : <Badge variant="secondary">ללא הגבלה</Badge>
                        }
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{(plan as any).officeCount ?? 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const visible = (plan.includedApps || []).filter(
                            (appPlan) => !isManagedChannelWallet(appPlan.appIdentifier),
                          );
                          return visible.length > 0 ? (
                            visible.map((appPlan, index) => {
                            const app = apps.find((a) => a.appIdentifier === appPlan.appIdentifier);
                            return (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {app?.name || appPlan.appIdentifier}
                              </Badge>
                            );
                            })
                          ) : (
                            <span className="text-slate-400">ללא אפליקציות</span>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(plan)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePlan(plan.planId!)}
                          disabled={isDeleting}
                          title="ארכיון"
                          aria-label="ארכיון"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
            </TabsContent>
            <TabsContent value="archived">
              {archivedLoading ? (
                <div className="text-center py-8 text-slate-600">טוען ארכיון...</div>
              ) : archivedPlans.length === 0 ? (
                <div className="text-center py-8 text-slate-600">אין תוכניות בארכיון</div>
              ) : (
                <div className="overflow-x-auto">
                <Table className="text-right">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">שם התוכנית</TableHead>
                      <TableHead className="text-right">מחיר חודשי</TableHead>
                      <TableHead className="text-right">משרדים</TableHead>
                      <TableHead className="text-right">פעולות</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedPlans.map((plan) => (
                      <TableRow key={plan.planId}>
                        <TableCell className="text-right font-medium">{plan.name}</TableCell>
                        <TableCell className="text-right">
                          {plan.priceMonthly === 0 ? <Badge variant="secondary">חינמי</Badge> : <span>₪{plan.priceMonthly}</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{plan.officeCount ?? 0}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isRestoring}
                            onClick={() => handleRestorePlan(plan.planId!)}
                          >
                            שחזר
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="flex h-[90vh] w-[95vw] max-w-6xl flex-col">
          <DialogHeader>
            <DialogTitle>עריכת תוכנית</DialogTitle>
            <DialogDescription>ערוך את פרטי התוכנית</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleEditPlan, (errors) => console.error("[PlansManagement] Validation errors:", JSON.stringify(errors, null, 2)))} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>שם התוכנית</FormLabel>
                        <FormControl>
                          <Input placeholder="לדוגמה: חבילה מקצועית" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="internalName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>שם פנימי</FormLabel>
                        <FormControl>
                          <Input placeholder="professional_plan" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>תיאור</FormLabel>
                      <FormControl>
                        <Input placeholder="תיאור קצר של התוכנית" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="quotaWindowDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ימי חלון קצובה</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isEditingFreePlan && (
                  <FormField
                    control={form.control}
                    name="periodDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ימי מנוי</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 30)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <PlanFeaturesFields
                  form={form}
                  connectors={connectors}
                />

                <CreditsSummary
                  watchedApps={watchedApps}
                  apps={apps}
                  quotaWindowDays={quotaWindowDays}
                  totalCredits={totalCredits}
                  totalWindowCredits={totalWindowCredits}
                  hasUnlimited={hasUnlimited}
                />

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-base font-medium">אפליקציות כלולות</Label>
                    <Select onValueChange={addAppToPlan}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="הוסף אפליקציה" />
                      </SelectTrigger>
                      <SelectContent>
                        {apps
                          .filter(
                            (app) =>
                              !isManagedChannelWallet(app.appIdentifier) &&
                              !appFields.some((field) => field.appIdentifier === app.appIdentifier),
                          )
                          .map((app) => (
                            <SelectItem key={app.appIdentifier} value={app.appIdentifier}>
                              {app.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {visibleAppFields.length > 0 ? (
                    <IncludedAppsTable
                      form={form}
                      appFields={appFields}
                      apps={apps}
                      watchedApps={watchedApps}
                      quotaWindowDays={quotaWindowDays}
                      removeApp={removeApp}
                    />
                  ) : (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <p className="text-slate-500">אין אפליקציות בתוכנית</p>
                      <p className="text-sm text-slate-400 mt-1">הוסף אפליקציה מהרשימה למעלה</p>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="flex-shrink-0 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    form.reset();
                  }}
                >
                  ביטול
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? "שומר..." : "שמור שינויים"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quotasPlan} onOpenChange={(open) => { if (!open) setQuotasPlan(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" dir="rtl">
          <DialogHeader>
            <DialogTitle>מכסות לפי אפליקציה</DialogTitle>
            <DialogDescription>
              מכסות חודשיות לכל אפליקציה בתוכנית {quotasPlan?.name || "—"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto space-y-3">
            <AppCreditsEditor
              rows={quotaDraft
                .filter((app) => !isManagedChannelWallet(app.appIdentifier))
                .map((app) => {
                const catalog = apps.find((item) => item.appIdentifier === app.appIdentifier);
                const unlimited = isAppUnlimited(app);
                const monthly = Number(app.includedCredits) || 0;
                const windowDays = quotasPlan?.quotaWindowDays ?? 3;
                return {
                  id: app.appIdentifier,
                  name: catalog?.name || app.appIdentifier,
                  subtitle: unlimited
                    ? `${app.appIdentifier} · ללא הגבלה`
                    : app.appIdentifier,
                  fields: [
                    {
                      key: "includedCredits",
                      value: unlimited ? UNLIMITED_INCLUDED_CREDITS : monthly,
                      min: 0,
                      readOnly: unlimited,
                    },
                    {
                      key: "windowCredits",
                      value: unlimited
                        ? UNLIMITED_INCLUDED_CREDITS
                        : windowCreditsForApp(monthly, windowDays),
                      min: 0,
                      readOnly: true,
                    },
                    { key: "creditCost", value: app.creditCost ?? 1, min: 0 },
                  ],
                };
              })}
              fieldLabels={{
                includedCredits: "קרדיטים כלולים",
                windowCredits: "קרדיטי חלון",
                creditCost: "עלות קרדיט",
              }}
              onChange={(appId, fieldKey, value) => {
                if (fieldKey === "windowCredits") return;
                setQuotaDraft((prev) =>
                  prev.map((app) =>
                    app.appIdentifier === appId
                      ? {
                          ...app,
                          [fieldKey]: value,
                          ...(fieldKey === "includedCredits"
                            ? { unlimited: false }
                            : {}),
                        }
                      : app,
                  ),
                );
              }}
              emptyText="אין אפליקציות בתוכנית"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQuotasPlan(null)}>
              ביטול
            </Button>
            <Button onClick={() => void handleSavePlanQuotas()} disabled={isUpdating || !quotasPlan}>
              {isUpdating ? "שומר..." : "שמירה"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansManagement;
